import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import useTrackPageView from '../../hooks/useTrackPageView';
import { PAGE_TYPE, CONTENT_TYPE } from '../../constants/gaEnums';
import { trackContentSave } from '../../lib/gaEvents';
import { Typography, Box, Stack, useMediaQuery, Divider } from '@mui/material';
import ProgressBar from '../../components/common/ProgressBar';
import CommentSection from '../../components/comment/CommentSection';
import { newsApi } from '../../api/news';
import PageLayout from '../../components/layout/PageLayout';
import NoData from '../../components/common/NoData';
import { useUserStore } from '../../stores/userStore';
import { commentApi } from '../../api/comment';
import EditDeleteMenuButton from '../../components/button/EditDeleteMenuButton';
import NewsSourceWithDate from '../../components/common/NewsSourceWithDate';
import CommentCount from '../../components/common/CommentCount';
import BookmarkCount from '../../components/common/BookmarkCount';
import theme from '../../theme';
import BookmarkButton from '../../components/button/BookmarkButton';
import ShareButton from '../../components/button/ShareButton';
import ContentViewer from '../../components/common/ContentViewer';
import SEO from '../../components/common/SEO';
import OpenInAppBanner from '../../components/common/OpenInAppBanner';
import SidebarLayout from '../../components/layout/SidebarLayout';
import InFeedBoxAd from '../../components/ad/InFeedBoxAd';
import { SITE_URL } from '../../utils/env';
import { Helmet } from 'react-helmet-async';

const NewsDetail = () => {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [news, setNews] = useState(null);
  useTrackPageView(
    {
      pageType: PAGE_TYPE.NEWS_POST,
      contentType: CONTENT_TYPE.NEWS,
      contentId: news?.id,
      contentName: news?.title,
    },
    { ready: !!news?.id },
  );
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const user = useUserStore((state) => state.user);
  const isAdmin = useUserStore((state) => state.isAdmin);
  const isXs = useMediaQuery(theme.breakpoints.down('md'));

  // 댓글 스크롤: path parameter에서 commentId 감지
  const commentViewMatch = location.pathname.match(/\/comment\/(\d+)$/);
  const commentScrollId = commentViewMatch ? parseInt(commentViewMatch[1]) : null;

  useEffect(() => {
    const fetchNewsDetail = async () => {
      try {
        // 숫자 ID이면 기존 방식, 슬러그이면 by-slug 엔드포인트 사용
        const isNumericId = /^\d+$/.test(slug);
        const newsRes = isNumericId
          ? await newsApi.getNewsById(slug)
          : await newsApi.getNewsBySlug(slug);
        const newsData = newsRes.data;
        const commentsRes = await commentApi.getComments('news', newsData.id);

        setComments(commentsRes.data);
        setNews(newsData);
        setIsBookmarked(newsData.isBookmarked);
      } catch (err) {
        console.error('news 상세 조회 실패하였습니다.', err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchNewsDetail();
  }, [slug]);

  const handleBookmarkToggle = async () => {
    try {
      if (isBookmarked) {
        await newsApi.deleteBookmark(news.id);
        setNews((prev) => ({
          ...prev,
          bookmarkCount: prev.bookmarkCount - 1,
        }));
      } else {
        await newsApi.addBookmark(news.id);
        setNews((prev) => ({
          ...prev,
          bookmarkCount: prev.bookmarkCount + 1,
        }));
        trackContentSave(
          {
            contentType: CONTENT_TYPE.NEWS,
            contentId: news.id,
            contentName: news.title,
            pageType: PAGE_TYPE.NEWS_POST,
          },
          user,
        );
      }
      setIsBookmarked(!isBookmarked);
    } catch (err) {
      console.error('bookmark 토글 실패하였습니다.', err.message);
    }
  };

  const handleEditClick = () => {
    navigate(`/news/${news?.id}/edit?from=detail`);
  };

  const handleDelete = async () => {
    try {
      await newsApi.deleteNews(news.id);
      navigate('/news');
    } catch (error) {
      console.error('Failed to delete news:', error);
    }
  };

  const handleCommentSubmit = async (content) => {
    const { data } = await newsApi.addComment(news.id, content);
    setNews((prev) => ({ ...prev, commentCount: prev.commentCount + 1 }));
    return data;
  };

  const handleCommentReply = async (parentId, content, mentionedUserId) => {
    const { data } = await newsApi.addComment(news.id, content, parentId, mentionedUserId);
    setNews((prev) => ({ ...prev, commentCount: prev.commentCount + 1 }));
    return data;
  };

  const handleCommentDelete = () => {
    setNews((prev) => ({ ...prev, commentCount: prev.commentCount - 1 }));
  };

  return (
    <PageLayout maxWidth={'980px'}>
      {news && (
        <>
          <OpenInAppBanner type="news" title={news.title} image={news.imageUrl} />
          <SEO
            title={news.title}
            description={news.content.replace(/<[^>]*>?/gm, '').substring(0, 160)} // HTML 태그 제거 후 길이 제한
            image={news.imageUrl}
            url={news.slug ? `${SITE_URL}/news/${news.slug}` : `${SITE_URL}/news/${news.id}`}
          />
          <Helmet>
            <script type="application/ld+json">
              {JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: 'Postpop', item: SITE_URL },
                  { '@type': 'ListItem', position: 2, name: 'News', item: `${SITE_URL}/news` },
                  { '@type': 'ListItem', position: 3, name: news.title },
                ],
              })}
            </script>
          </Helmet>
          <Helmet>
            <script type="application/ld+json">
              {JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'NewsArticle',
                headline: news.title,
                image: news.imageUrl ? [news.imageUrl] : undefined,
                datePublished: news.releaseDate?.replace(
                  /(\d+)\.(\d+)\.(\d+)/,
                  (_, y, m, d) => `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`,
                ),
                dateModified: news.updatedAt?.replace(
                  /(\d+)\.(\d+)\.(\d+)/,
                  (_, y, m, d) => `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`,
                ),
                author: {
                  '@type': 'Organization',
                  name: 'Postpop',
                  url: SITE_URL,
                },
              })}
            </script>
          </Helmet>
        </>
      )}
      <ProgressBar isLoading={loading}>
        {!news ? (
          <NoData />
        ) : (
          <SidebarLayout pageType={PAGE_TYPE.NEWS_POST}>
            {/* 헤더 */}
            <Box
              sx={{
                pb: { xs: '16px', lg: '24px' },
                borderBottom: `1px solid ${theme.palette.grey.line}`,
              }}
            >
              {/* 제목 */}
              <Typography
                fontSize={{ xs: 16, md: 18 }}
                fontWeight={'bold'}
                sx={{ lineHeight: { xs: '1.5', md: '1.15' } }}
              >
                {news.title}
              </Typography>

              {/* 출처/날짜/댓글/북마크 + 버튼 */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                  mt: { xs: '16px', md: '-6px' },
                }}
              >
                {/* 좌: 출처, 날짜, 댓글/북마크 */}
                <Box>
                  <Box display="flex" alignItems="center" gap={1} sx={{ flexWrap: 'wrap' }}>
                    <NewsSourceWithDate
                      linkUrl={news.linkUrl}
                      source={news.sourceUrl}
                      date={news.releaseDate}
                      withDate={!isXs}
                      contentId={news.id}
                    />
                    {!isXs && (
                      <>
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{
                            height: '8px',
                            alignSelf: 'center',
                            backgroundColor: theme.palette.grey.line,
                          }}
                        />
                        <Box display="flex" gap={1.5} alignItems="center">
                          <CommentCount commentCount={news.commentCount} />
                          <BookmarkCount bookmarkCount={news.bookmarkCount} />
                        </Box>
                      </>
                    )}
                  </Box>
                  {isXs && (
                    <Box
                      display="flex"
                      direction="row"
                      gap={1}
                      alignItems="center"
                      sx={{ mt: '16px' }}
                    >
                      {isXs && (
                        <>
                          <Typography
                            fontSize={14}
                            sx={{ color: theme.palette.black[4], whiteSpace: 'nowrap' }}
                          >
                            {news.releaseDate}
                          </Typography>
                          <Divider
                            orientation="vertical"
                            flexItem
                            sx={{
                              height: '8px',
                              alignSelf: 'center',
                              backgroundColor: theme.palette.grey.line,
                            }}
                          />
                        </>
                      )}
                      <Box display="flex" gap={1.5} alignItems="center">
                        <CommentCount commentCount={news.commentCount} />
                        <BookmarkCount bookmarkCount={news.bookmarkCount} />
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* 우: 버튼 */}
                <Box display={'flex'} gap={1}>
                  <ShareButton
                    url={`/news/${news.id}`}
                    contentType={CONTENT_TYPE.NEWS}
                    contentId={news.id}
                    contentName={news.title}
                    shareText={news.title}
                    pageType={PAGE_TYPE.NEWS_POST}
                  />
                  <BookmarkButton isBookmarked={isBookmarked} onToggle={handleBookmarkToggle} />
                  {isAdmin && (
                    <EditDeleteMenuButton onEdit={handleEditClick} onDelete={handleDelete} />
                  )}
                </Box>
              </Box>
            </Box>

            <Stack spacing={'30px'} sx={{ mt: { xs: '16px', md: '24px', lg: '30px' } }}>
              {/* 본문 */}
              <ContentViewer text={news.content} />
              {/* 광고 */}
              <InFeedBoxAd pageType={PAGE_TYPE.NEWS_POST} />
              {/* 댓글 */}
              <CommentSection
                commentData={comments}
                articleType="news"
                articleId={news.id}
                contentName={news.title}
                onSubmit={handleCommentSubmit}
                onReply={handleCommentReply}
                onDelete={handleCommentDelete}
                commentScrollId={commentScrollId}
              />
            </Stack>
          </SidebarLayout>
        )}
      </ProgressBar>
    </PageLayout>
  );
};

export default NewsDetail;
